import type {RefObject} from 'react';
import {type AnimationControls, m as motion, type TargetAndTransition, type Transition} from 'motion/react';
import useDarkMode from 'src/hooks/useDarkMode';
import BaseButton from '../BaseButton';

const lightStyles: Record<string, AnimationControls | TargetAndTransition> = {
  correctSelected: {
    backgroundColor: [
      'rgba(34, 197, 94, 0)',
      'rgba(34, 197, 94, 0.05)',
      'rgba(34, 197, 94, 0.15)',
      'rgba(34, 197, 94, 0.15)',
      'rgba(34, 197, 94, 0.15)'
    ],
    outlineWidth: ['0px', '0px', '1px', '8px', '4px'],
    outlineColor: [
      'rgba(34, 197, 94, 0)',
      'rgba(34, 197, 94, 0)',
      'rgba(34, 197, 94, 0)',
      'rgba(19, 118, 55, 0.8)',
      'rgba(19, 118, 55, 0.6)'
    ],
    color: [
      'rgba(40, 40, 40, 1)',
      'rgba(40, 60, 47, 1)',
      'rgba(19, 118, 55, 1)',
      'rgba(19, 118, 55, 1)',
      'rgba(19, 118, 55, 1)'
    ]
  },
  correct: {
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    outlineColor: ['rgba(19, 118, 55, 0)', 'rgba(19, 118, 55, 0.6)'],
    outlineWidth: ['4px', '4px', '4px', '4px', '4px'],
    color: 'rgba(19, 118, 55, 1)'
  },
  incorrect: {
    outlineWidth: ['0px', '0px', '6px', '4px', '4px'],
    outlineColor: [
      'rgba(198, 50, 50, 0)',
      'rgba(198, 50, 50, 0.15)',
      'rgba(198, 50, 50, 0.6)',
      'rgba(198, 50, 50, 0.1)',
      'rgba(198, 50, 50, 1)'
    ]
  },
  default: {
    backgroundColor: 'rgba(34, 197, 94, 0)'
  }
};

const darkStyles: Record<string, AnimationControls | TargetAndTransition> = {
  correctSelected: {
    backgroundColor: [
      'rgba(34, 197, 94, 0)',
      'rgba(34, 197, 94, 0.15)',
      'rgba(34, 197, 94, 1)',
      'rgba(34, 197, 94, 1)',
      'rgba(34, 197, 94, 1)'
    ],
    outlineWidth: ['0px', '0px', '1px', '8px', '4px'],
    outlineColor: [
      'rgba(34, 197, 94, 0)',
      'rgba(34, 197, 94, 0)',
      'rgba(34, 197, 94, 0)',
      'rgba(34, 197, 94, 0.4)',
      'rgba(34, 197, 94, 0.3)'
    ]
  },
  correct: {
    backgroundColor: 'rgba(34, 197, 94, 1)'
  },
  incorrect: {
    outlineWidth: ['0px', '0px', '6px', '4px', '4px'],
    outlineColor: [
      'rgba(239, 68, 68, 0)',
      'rgba(239, 68, 68, 0.15)',
      'rgba(239, 68, 68, 0.6)',
      'rgba(239, 68, 68, 0.1)',
      'rgba(239, 68, 68, 1)'
    ]
  },
  default: {
    backgroundColor: 'rgba(34, 197, 94, 0)'
  }
};

const transitions: Record<string, Transition> = {
  correctSelected: {
    duration: 1,
    times: [0, 0.2, 0.25, 0.35, 0.8],
    ease: 'easeInOut'
  },
  correct: {
    duration: 1,
    ease: 'easeInOut',
    delay: 0.6
  },
  incorrect: {
    duration: 1,
    times: [0, 0.2, 0.3, 0.6, 0.9],
    ease: 'easeInOut'
  },
  default: {}
};

type Props = {
  text: string;
  onAnswerClick: (text: string) => void;
  index: number;
  ref?: RefObject<HTMLDivElement>;
  state: 'correctSelected' | 'correct' | 'incorrect' | undefined;
};

const QuizAnswerButton = ({text, onAnswerClick, index, ref, state}: Props) => {
  const {isDark} = useDarkMode();

  const styles = isDark ? darkStyles : lightStyles;

  return (
    <motion.div
      ref={ref}
      onAnimationComplete={() => {
        if (index === 0) ref?.current?.querySelector('button')?.focus();
      }}
      className="grow flex"
      initial={{opacity: 0, y: 40}}
      animate={{
        opacity: 1,
        y: 0,
        transition: {delay: 0.5 + index * 0.05, duration: 0.4, ease: [0.2, 0.92, 0.34, 1]}
      }}
      exit={{opacity: 0, y: 40}}
    >
      <BaseButton
        type="button"
        className="grow font-heading font-medium text-lg max-sm:text-base text-(--quiz-text-color) overflow-visible inset-shadow-[0_1px_1px_0_rgb(255_255_255_/_0.5)] shadow-2xl dark:inset-shadow-[0_0_0_1px_rgb(245_225_255_/_0.12)] backdrop-blur-sm transition-colors bg-[rgb(250_250_250_/_0.5)] dark:bg-[rgb(255_255_255_/_0.12)] hover:bg-[rgb(255_255_255_/_0.7)] dark:hover:bg-[rgb(255_255_255_/_0.2)] data-pressed:brightness-[0.98] !p-0 items-stretch w-full data-focus-visible:ring-3"
        size="large"
        onPress={() => onAnswerClick(text)}
      >
        <motion.div
          className="px-3 max-md:px-2 shrink flex items-center justify-center rounded-full outline-offset-0 outline grow"
          style={{outlineWidth: 0}}
          animate={styles[state || 'default']}
          transition={transitions[state || 'default']}
        >
          <span className="line-clamp-1 dark:[text-shadow:_0_1px_1px_rgb(0_0_0_/_0.2)]">{text}</span>
        </motion.div>
      </BaseButton>
    </motion.div>
  );
};

export default QuizAnswerButton;
